/**
 * JourneyScreen — tela principal da Jornada de 6 fases.
 *
 * F1-FE-1: Wizard com 5–7 etapas navegáveis (dependendo de brownfield/greenfield),
 * ligadas à API real via react-query. Import e Ideation são placeholders neste lote.
 *
 * Estrutura:
 *   JourneyProvider (estado da jornada + sessionStorage)
 *   └── JourneyStepper (chips de fase + dots de etapa)
 *   └── Card principal com a etapa atual:
 *       step = 'project'      → StepProject
 *       step = 'git'          → StepGit (F1-FE-2)
 *       step = 'import'       → StepImportPlaceholder
 *       step = 'ideation'     → StepIdeationPlaceholder (greenfield only)
 *       step = 'context'      → StepContext
 *       step = 'dataBiz'      → StepDataBiz
 *       step = 'techCriteria' → StepTechCriteria
 *       step = 'done'         → tela de conclusão
 *
 * Navegação: onNext() → completeStep(), onBack() → goToStep(prevStep).
 * Paleta C, estilos inline, pt-BR, a11y.
 */
import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNavigationContext } from '@/navigation';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { JourneyProvider, useJourney, STEP_META } from './JourneyContext';
import { useJourneyPresence } from '@/api/agents';
import { useProjectJourney } from '@/api/journey';
import { JourneyStepper } from './JourneyStepper';
import { StepProject } from './steps/StepProject';
import { StepGit } from './steps/StepGit';
import { StepImport } from './steps/StepImport';
import { StepIdeation } from './steps/StepIdeation';
import { StepContext } from './steps/StepContext';
import { StepAderencia } from './steps/StepAderencia';
import { StepDataBiz } from './steps/StepDataBiz';
import { StepTechCriteria } from './steps/StepTechCriteria';
import { StepSquadDesigner } from './steps/StepSquadDesigner';
import { DesignStudioScreen } from '../DesignStudioScreen';
import { GateNegocioScreen } from '../gates/GateNegocioScreen';
import { GateTecnologiaScreen } from '../gates/GateTecnologiaScreen';
import { GateSquadsScreen } from '../gates/GateSquadsScreen';
import { GateEscopoScreen } from '../gates/GateEscopoScreen';
import { BlueprintScreen } from '../BlueprintScreen';
import { ArtifactsScreen } from '../ArtifactsScreen';
import { ComponentsScreen } from '../ComponentsScreen';
import { BacklogScreen } from '../BacklogScreen';
import { CyclesScreen } from '../CyclesScreen';
import { useCommitBacklog } from '@/api/backlog';
import { RepoGraphEmbed } from '@/components/RepoGraphEmbed';
import { CheckCircle2 } from 'lucide-react';

/** Envolve uma tela da jornada com rodapé de navegação (Voltar/Continuar).
 * O `onNext` recebido (via commonProps) já dispara a CONFIRMAÇÃO global antes de
 * avançar (ver StepConfirmContext em JourneyScreen). Aqui só rodamos o beforeNext
 * (save) e chamamos onNext. */
function StepShell({ onBack, onNext, beforeNext, children }) {
  const [busy, setBusy] = useState(false);
  // beforeNext (opcional): persiste o estado da etapa ANTES de avançar. Usado pelo
  // Blueprint p/ garantir que "Continuar" salva TODA a arquitetura na memória.
  async function handleNext() {
    if (beforeNext) {
      try { setBusy(true); await beforeNext(); }
      catch { /* não bloqueia a navegação se o save falhar */ }
      finally { setBusy(false); }
    }
    onNext();
  }
  return (
    <div>
      {children}
      <div style={{ display: 'flex', gap: 10, marginTop: 24, paddingTop: 16,
        borderTop: `1px solid ${C.blackBorder}` }}>
        <button onClick={onBack} disabled={busy} style={{ background: 'transparent', color: C.neutralDim,
          border: `1px solid ${C.blackBorder}`, borderRadius: 8, padding: '9px 18px', fontSize: 13, cursor: 'pointer' }}>
          ← Voltar
        </button>
        <button onClick={handleNext} disabled={busy} style={{ marginLeft: 'auto', background: C.cyber, color: C.black,
          border: 'none', borderRadius: 8, padding: '9px 22px', fontSize: 13, fontWeight: 700,
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Salvando…' : 'Continuar →'}
        </button>
      </div>
    </div>
  );
}

// ─── DoneScreen ──────────��──────────────────────────────��─────────────────────

function DoneScreen() {
  const { navigate } = useNavigationContext();
  const { reset } = useJourney();

  function handleNewJourney() {
    reset();
    navigate('journey');
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '48px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: `${C.cyber}22`,
          border: `2px solid ${C.cyber}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 24,
        }}
      >
        <CheckCircle2 size={28} style={{ color: C.cyber }} aria-hidden="true" />
      </div>

      <Badge color={C.cyber} light>WIZARD CONCLUÍDO</Badge>

      <h2 style={{ fontSize: 22, fontWeight: 600, color: C.neutral, margin: '16px 0 8px' }}>
        Projeto configurado com sucesso
      </h2>
      <p style={{ fontSize: 14, color: C.neutralDim, lineHeight: 1.6, maxWidth: 520, margin: '0 0 12px' }}>
        A configuração (Definições, Negócio, Tecnologia e Ciclos) está concluída.
        As fases <strong style={{ color: C.neutral }}>Definição dos Squads</strong> e{' '}
        <strong style={{ color: C.neutral }}>Execução</strong> acontecem AGORA, ao iniciar:
        o pipeline monta o squad automaticamente e você o ajusta no Squad Designer
        antes do Gate A. (Por isso o wizard vai da Tecnologia direto para os Ciclos —
        squad e execução são a etapa de RUN, não de setup.)
      </p>

      <div className="flex items-center gap-3" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
        <Btn variant="primary" size="lg" onClick={() => navigate('dashboard')}>
          Ir para o Dashboard
        </Btn>
        <Btn variant="ghost" size="md" onClick={handleNewJourney}>
          Nova jornada
        </Btn>
      </div>
    </div>
  );
}

// ─── WizardBody — renderiza a etapa atual ────────���────────────────────────────

function WizardBody() {
  const { current, steps, currentIndex, completeStep, goToStep, projectId } = useJourney();
  // Ref para o "save" do Blueprint — o Continuar chama antes de avançar, garantindo
  // que TODA a arquitetura (stack + serviços + arquitetura) vá para a memória.
  const blueprintSaveRef = useRef(null);
  // "Próximo" do Backlog/Ciclos persiste o escopo (backlog+ciclos) na memória/RAG
  // antes do Gate de Escopo e da execução.
  const commitBacklog = useCommitBacklog(projectId);
  // Confirmação GLOBAL antes de avançar: qualquer passo (via commonProps.onNext)
  // pede confirmação de que os parâmetros foram carregados/aplicados/salvos. As
  // pessoas estavam clicando "Continuar" sem salvar (ex.: sem indexar o GitHub).
  const [pendingNext, setPendingNext] = useState(false);

  if (current === 'done') {
    return <DoneScreen />;
  }

  function goBack() {
    if (currentIndex > 0) {
      goToStep(steps[currentIndex - 1]);
    }
  }

  const commonProps = {
    onNext: () => setPendingNext(true),   // abre a confirmação global (renderizada abaixo)
    onBack: goBack,
  };

  const stepEl = (() => {
  switch (current) {
    case 'project':
      return <StepProject {...commonProps} />;
    case 'git':
      return <StepGit {...commonProps} />;
    case 'graph':
      return (
        <StepShell {...commonProps}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
              Análise do Repositório
            </h2>
            <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
              Grafo de conhecimento (Graphify) do repositório mapeado — dependências, módulos e
              relações reais do código. Revise a análise a qualquer momento.
            </p>
          </div>
          <RepoGraphEmbed projectId={projectId} minHeight={560} />
        </StepShell>
      );
    case 'import':
      return <StepImport {...commonProps} />;
    case 'ideation':
      return <StepIdeation {...commonProps} />;
    case 'context':
      return <StepContext {...commonProps} />;
    case 'dataBiz':
      return <StepDataBiz {...commonProps} />;
    case 'techCriteria':
      return <StepTechCriteria {...commonProps} />;
    case 'squad':
      return <StepSquadDesigner {...commonProps} />;
    case 'designStudio':
      return <StepShell {...commonProps}><DesignStudioScreen projectId={projectId} /></StepShell>;
    case 'gateNegocio':
      // Gate de aprovação — NÃO usa StepShell (o "Continuar" genérico puluaria o
      // gate). Só avança para o Blueprint depois de APROVAR (via onContinue).
      // onBack permite voltar à etapa anterior sem precisar aprovar (o gate não
      // tinha botão Voltar e o usuário ficava preso na tela).
      return <GateNegocioScreen projectId={projectId} embedded onBack={goBack} onContinue={completeStep} />;
    case 'aderencia':
      // Aderência ao Escopo (o que pediu × o que geramos) — logo após o Gate 1.
      return <StepAderencia {...commonProps} />;
    case 'gateTecnologia':
      // Gate 2 · Tecnologia — idem: só avança para o Squad Designer após APROVAR.
      return <GateTecnologiaScreen projectId={projectId} embedded onBack={goBack} onContinue={completeStep} />;
    case 'gateSquads':
      // Gate 3 · Squads — aprova a composição do squad; só então libera os Ciclos.
      return <GateSquadsScreen projectId={projectId} embedded onBack={goBack} onContinue={completeStep} />;
    case 'gateEscopo':
      // Gate 4 · Escopo — valida backlog/dependências/ciclos; só então libera a execução.
      return <GateEscopoScreen projectId={projectId} embedded onBack={goBack} onContinue={completeStep} />;
    case 'blueprint':
      return (
        <StepShell {...commonProps} beforeNext={() => blueprintSaveRef.current?.()}>
          <BlueprintScreen projectId={projectId} saveRef={blueprintSaveRef} />
        </StepShell>
      );
    case 'artifacts':
      return <StepShell {...commonProps}><ArtifactsScreen projectId={projectId} /></StepShell>;
    case 'components':
      return <StepShell {...commonProps}><ComponentsScreen projectId={projectId} /></StepShell>;
    case 'backlog':
      return (
        <StepShell {...commonProps} beforeNext={() => commitBacklog.mutateAsync()}>
          <BacklogScreen projectId={projectId} />
        </StepShell>
      );
    case 'cycles':
      return (
        <StepShell {...commonProps} beforeNext={() => commitBacklog.mutateAsync()}>
          <CyclesScreen projectId={projectId} />
        </StepShell>
      );
    default:
      return (
        <div style={{ padding: 24, color: C.neutralMuted, fontSize: 14 }}>
          Etapa "{current}" em desenvolvimento.
        </div>
      );
  }
  })();

  return (
    <>
      {stepEl}
      {pendingNext && (
        <div role="dialog" aria-modal="true" aria-labelledby="next-confirm-title"
          onClick={() => setPendingNext(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, width: '100%',
            background: C.blackElev, border: `1px solid ${C.blackBorder}`, borderRadius: 12, padding: 22,
            boxShadow: '0 12px 40px rgba(0,0,0,.5)' }}>
            <div id="next-confirm-title" style={{ fontSize: 15, fontWeight: 700, color: '#E8E8E8', marginBottom: 10 }}>
              Antes de continuar
            </div>
            <p style={{ fontSize: 13.5, color: '#B8B8B0', lineHeight: 1.6, margin: '0 0 8px' }}>
              Confirme que você <strong style={{ color: '#E8E8E8' }}>carregou e aplicou</strong> os
              parâmetros necessários deste passo (indexar, salvar, aplicar as fontes). O próximo
              passo <strong style={{ color: '#E8E8E8' }}>depende disso</strong> — se algo ainda não
              foi salvo, volte e revise.
            </p>
            {STEP_CONFIRM_HINTS[current] && (
              <p style={{ fontSize: 13, color: C.cyber, lineHeight: 1.5, margin: '0 0 4px',
                background: 'rgba(1,230,228,.08)', border: '1px solid rgba(1,230,228,.25)',
                borderRadius: 8, padding: '9px 12px' }}>
                ⚠️ {STEP_CONFIRM_HINTS[current]}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => setPendingNext(false)} style={{ background: 'transparent', color: C.neutralDim,
                border: `1px solid ${C.blackBorder}`, borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>
                Voltar e revisar
              </button>
              <button onClick={() => { setPendingNext(false); completeStep(); }}
                style={{ marginLeft: 'auto', background: C.cyber, color: C.black, border: 'none',
                  borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Sim, já apliquei — continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Dica específica por passo mostrada na confirmação (lembra o pré-requisito).
const STEP_CONFIRM_HINTS = {
  git: 'Você conectou E INDEXOU o repositório do GitHub? Sem a indexação concluída, o próximo passo não terá o código para analisar.',
  import: 'Você importou e aplicou as fontes/itens necessários?',
  ideation: 'Você salvou a ideação?',
  context: 'Você salvou o contexto do projeto?',
  dataBiz: 'Você salvou/aplicou o escopo de negócio (e as fontes indexadas)?',
  techCriteria: 'Você aplicou os critérios técnicos?',
  squad: 'Você salvou a composição do squad?',
  blueprint: 'Você revisou e o Blueprint será salvo ao continuar. Confira a stack/serviços.',
  backlog: 'Você revisou o backlog? Ele será persistido ao continuar.',
  cycles: 'Você revisou os ciclos? Serão persistidos ao continuar.',
};

// ─── JourneyContent — usa o contexto ─────────────────────────────────────────

function JourneyContent() {
  const { current, projectId, openAt } = useJourney();
  const [searchParams] = useSearchParams();
  // Nome do projeto p/ o header (a jornada retorna `name`). Sem projectId a query
  // fica desabilitada. Assim o usuário SEMPRE vê de qual projeto é a jornada.
  const { data: journeyData } = useProjectJourney(projectId);
  const projectName = journeyData?.name;

  // Deep-link da Gestão do Projeto: /journey?project=<id>&step=<step>&type=<tipo>.
  // Abre a jornada DESTE projeto no passo pedido, com tudo navegável para revisão.
  // (Sem ?project= mantém o comportamento normal do wizard via sessionStorage.)
  const dlProject = searchParams.get('project');
  const dlStep    = searchParams.get('step');
  const dlType    = searchParams.get('type');
  const dlScope   = searchParams.get('scope');
  useEffect(() => {
    if (!dlProject) return;
    openAt({
      projectId: dlProject,
      step: dlStep || undefined,
      projectType: dlType || undefined,
      deliveryScope: dlScope || undefined,
    });
  }, [dlProject, dlStep, dlType, dlScope, openAt]);

  // Presença: enquanto o usuário está na configuração, mantém o squad do projeto
  // ATIVO na Sala dos Agentes (heartbeat que refresca os pings no Redis).
  useJourneyPresence(projectId);

  const isDone = current === 'done';

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div
          style={{
            fontSize: 12,
            color: C.neutralDim,
            letterSpacing: '0.06em',
            marginBottom: 6,
          }}
        >
          JORNADA DO PROJETO{projectName ? ` · ${projectName}` : ''}
        </div>
        <div className="flex items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: C.neutral,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            {STEP_META[current]?.label ?? (isDone ? 'Concluído' : 'Jornada')}
          </h1>
          {projectName && (
            <Badge color={C.cyberDim} light>{projectName}</Badge>
          )}
          {projectId && (
            <Badge color={C.neutralMuted} light>
              ID: {projectId.slice(0, 8)}
            </Badge>
          )}
        </div>
      </div>

      {/* Stepper — não exibe quando conclu��do */}
      {!isDone && <JourneyStepper />}

      {/* Card da etapa atual */}
      <Card style={{ padding: isDone ? 0 : 28 }}>
        <WizardBody />
      </Card>
    </div>
  );
}

// ──��� JourneyScreen — ponto de entrada público ──���──────────────────────────────

export function JourneyScreen() {
  return (
    <JourneyProvider>
      <JourneyContent />
    </JourneyProvider>
  );
}
